import {
	filtersPolishSelector,
	isInFiltersPolishExperiment,
} from '@client/redux/experiments/selectors/filters-polish';
import SaveButton from '@components/shared/PillWithDropdown/Dropdown/components/SaveButton';
import {
	default as BaseStyles,
	default as DropdownStyles,
} from '@components/shared/PillWithDropdown/Dropdown/styles.scss';
import Pill from '@components/shared/PillWithDropdown/Pill';
import { MEDIA_QUERY_STATES } from '@constants/media-query';
import { useIsImproveMpPerformanceTier2Enabled } from '@hooks/use-improve-mp-performance-tier-2';
import { useIsOnSemanticSearch } from '@hooks/use-is-on-semantic-search';
import { useTrackOpenFilterMenu } from '@hooks/use-track-open-filter-menu';
import { useUpdateFilterValues } from '@pages/VendorsSearch/containers/FilterPills/hooks/use-update-filter-values';
import type { PricingFilterStrategy } from '@pages/VendorsSearch/containers/FilterPills/strategies/pricing-filter-strategy';
import { reportServerSideExperiment } from '@redux/experiments';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { experiments } from '@settings';
import { useMedia } from '@tkww/orion-web-app-kernel';
import { Link } from '@tkww/orion-web-ds-action';
import { Logger } from '@utils/logger/logger';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { afterNextPaint } from '../../../../../../../helpers/after-next-paint';
import { useTrackSlider } from '../../hooks/use-track-slider';
import useClickListenerOnPillDropdown from '../../hooks/useClickListenerOnPillDropdown';
import { isDirtyMinMaxAtom } from './atoms';
import { StartingPriceFilter } from './starting-price-filter';
import Styles from './styles.scss';
import { useStartingPriceFilter } from './use-starting-price-filter';

interface StartingPriceFilterDropdownProps {
	closeDropdown: () => void;
	isDesktop: boolean;
}

const StartingPriceFilterDropdown = ({
	closeDropdown,
	isDesktop,
}: StartingPriceFilterDropdownProps) => {
	const {
		clear,
		startingMin,
		startingMax,
		step,
		minInput,
		maxInput,
		minVal,
		maxVal,
		setMaxInput,
		setMaxVal,
		setMinInput,
		setMinVal,
		filterValue,
		intervals,
	} = useStartingPriceFilter();
	const { updateFilterValues } = useUpdateFilterValues();

	const isDirtyMinMax = useAtomValue(isDirtyMinMaxAtom);
	const trackSlider = useTrackSlider();
	const { isOnSemanticSearch } = useIsOnSemanticSearch();
	const filtersPolishAssignment = useAppSelector(filtersPolishSelector);
	const isFilterPolishExperimentEnabled = useAppSelector(
		isInFiltersPolishExperiment,
	);
	const isFilterPolishEnabled = isDesktop && isFilterPolishExperimentEnabled;
	const dispatch = useAppDispatch();

	useEffect(() => {
		if (isDesktop && filtersPolishAssignment) {
			Logger.log({
				log_message: `Filters Polish Assignment: ${filtersPolishAssignment}`,
			});

			void dispatch(reportServerSideExperiment(experiments.filtersPolish));
		}
	}, [filtersPolishAssignment, isDesktop, dispatch]);

	const onClickSave = () => {
		afterNextPaint(() => {
			updateFilterValues([
				{
					id: 'starting-price',
					value: filterValue,
				},
			]);

			trackSlider({
				action: 'save selected filters',
				min: minVal,
				max: maxVal,
				sourcePage: isOnSemanticSearch
					? 'semantic_results'
					: 'category results',
			});
			closeDropdown();
		});
	};

	const onClickClear = () => {
		afterNextPaint(() => {
			trackSlider({
				action: 'clear selected filters',
				min: minVal,
				max: maxVal,
				sourcePage: isOnSemanticSearch
					? 'semantic_results'
					: 'category results',
			});
			clear();
		});
	};

	return (
		<>
			<div className={Styles.pillDropdown}>
				<StartingPriceFilter
					startingMax={startingMax}
					startingMin={startingMin}
					minVal={minVal}
					maxVal={maxVal}
					setMinVal={setMinVal}
					setMaxVal={setMaxVal}
					minInput={minInput}
					maxInput={maxInput}
					setMinInput={setMinInput}
					setMaxInput={setMaxInput}
					step={step}
					intervals={intervals}
				/>
			</div>
			<div className={Styles.border}>
				<div
					className={classNames(DropdownStyles.ctaSection, {
						[DropdownStyles['cta-section--filter-polish']]:
							isFilterPolishEnabled,
					})}
				>
					<Link
						as="button"
						className={DropdownStyles.clear}
						type="reset"
						onClick={onClickClear}
					>
						Clear
					</Link>
					<SaveButton
						disabled={!isDirtyMinMax}
						stagedOptions={[`${minInput} - ${maxInput}`]}
						onSave={onClickSave}
						isDesktopDropdown={isFilterPolishEnabled}
					>
						{isFilterPolishEnabled ? 'Apply' : 'Save'}
					</SaveButton>
				</div>
			</div>
		</>
	);
};

export interface StartingPriceFilterPillProps {
	appliedFilter?: PricingFilterStrategy;
}

export const StartingPriceFilterPill = ({
	appliedFilter,
}: StartingPriceFilterPillProps) => {
	const isDesktop = useMedia('@lg') === MEDIA_QUERY_STATES.matched;
	const { resetStagedMinMax } = useStartingPriceFilter();
	const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
	const isImproveMpPerformanceTier2Enabled =
		useIsImproveMpPerformanceTier2Enabled();
	const isSelected = useMemo(
		() => Boolean(dropdownOpen || appliedFilter),
		[dropdownOpen, appliedFilter],
	);
	const categoryCode = useAppSelector((state) => state.category.code);
	const marketCode = useAppSelector((state) => state.location.marketCode);
	const containerRef = useRef<HTMLDivElement>(null);
	const trackOpenFilterMenu = useTrackOpenFilterMenu();
	const onClick = useCallback(() => {
		afterNextPaint(() => {
			if (dropdownOpen) {
				closeDropdownAndReset();
			} else {
				setDropdownOpen(true);
				trackOpenFilterMenu('Price Filter', {
					vendorCategoryCode: categoryCode,
					displayMarketCode: marketCode,
				});
			}
		});
	}, [dropdownOpen, trackOpenFilterMenu, categoryCode, marketCode]);
	const closeDropdown = () => {
		setDropdownOpen(false);
	};
	const closeDropdownAndReset = () => {
		setDropdownOpen(false);
		resetStagedMinMax();
	};

	useClickListenerOnPillDropdown({
		closeDropdown: closeDropdownAndReset,
		containerRef,
		dropdownOpen,
		isMobile: !isDesktop,
	});

	return (
		<div ref={containerRef} className={Styles.pill}>
			<Pill
				label="Starting Price"
				onClick={onClick}
				selected={isSelected}
				isImproveMpPerformanceTier2Enabled={isImproveMpPerformanceTier2Enabled}
			/>
			{dropdownOpen && (
				<div className={`${BaseStyles.dropdown}`}>
					<StartingPriceFilterDropdown
						closeDropdown={closeDropdown}
						isDesktop={isDesktop}
					/>
				</div>
			)}
		</div>
	);
};
